import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";

function CommentItem({ comment, depth, auth, replyingTo, setReplyingTo, onSubmitReply, submitting, formatDate }) {
  return (
    <div className={`${depth > 0 ? "ml-8 border-l-2 border-[#E8E4DE] pl-6" : ""}`}>
      <div className="bg-white rounded-xl border border-[#E8E4DE] p-5 mb-4">
        <div className="flex items-center gap-3 mb-3">
          {comment.user_profile_photo ? (
            <img 
              src={`${API.replace('/api', '')}${comment.user_profile_photo}`}
              alt={comment.user_name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E6E2DD] to-[#A87C6D]/30 flex items-center justify-center">
              <span className="text-sm font-medium text-[#A87C6D]">
                {comment.user_name?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
          )}
          <div>
            <p className="font-medium text-[#2D2A26] text-sm">{comment.user_name}</p>
            <p className="text-xs text-[#6E6A66]">{formatDate(comment.created_at)}</p>
          </div>
        </div>
        <p className="text-[#4A4744] text-sm leading-relaxed">{comment.content}</p>
        
        {auth?.user && depth < 2 && (
          <button
            onClick={() => setReplyingTo({ id: comment.comment_id, name: comment.user_name, text: "" })}
            className="mt-3 text-xs text-[#A87C6D] hover:text-[#96695C] transition-colors"
          >
            Reply
          </button>
        )}

        {replyingTo?.id === comment.comment_id && (
          <form onSubmit={(e) => onSubmitReply(e, comment.comment_id)} className="mt-4">
            <textarea
              value={replyingTo.text}
              onChange={(e) => setReplyingTo({ ...replyingTo, text: e.target.value })}
              placeholder={`Reply to ${comment.user_name}...`}
              rows={3}
              className="w-full px-4 py-3 rounded-lg border border-[#E8E4DE] focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D] text-sm resize-none"
            />
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => setReplyingTo(null)} className="px-3 py-1.5 text-xs text-[#4A4744] hover:bg-[#F5F2EE] rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting || !replyingTo.text?.trim()} className="px-3 py-1.5 text-xs bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] disabled:opacity-50 transition-colors">
                {submitting ? "Submitting..." : "Reply"}
              </button>
            </div>
          </form>
        )}
      </div>

      {comment.replies?.map(reply => (
        <CommentItem
          key={reply.comment_id}
          comment={reply}
          depth={depth + 1}
          auth={auth}
          replyingTo={replyingTo}
          setReplyingTo={setReplyingTo}
          onSubmitReply={onSubmitReply}
          submitting={submitting}
          formatDate={formatDate}
        />
      ))}
    </div>
  );
}

export default function BlogPost({ auth }) {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }, []);

  const fetchPost = useCallback(async () => {
    try {
      const response = await fetch(`${API}/blog/posts/${slug}`);
      if (response.ok) {
        setPost(await response.json());
      } else {
        setPost(null);
      }
    } catch (err) {
      console.error("Failed to fetch post:", err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchComments = useCallback(async (postId) => {
    if (!postId) return;
    try {
      const response = await fetch(`${API}/blog/posts/${postId}/comments`);
      if (response.ok) {
        setComments((await response.json()).comments);
      }
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    }
  }, []);

  useEffect(() => { fetchPost(); }, [fetchPost]);
  useEffect(() => { if (post?.post_id) fetchComments(post.post_id); }, [post?.post_id, fetchComments]);

  const handleSubmitComment = async (e, parentId = null) => {
    e.preventDefault();
    if (!auth?.user) { toast.error("Please sign in to comment"); return; }
    const text = parentId ? replyingTo?.text : commentText;
    if (!text?.trim()) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/blog/posts/${post.post_id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: text.trim(), parent_comment_id: parentId })
      });
      if (response.ok) {
        toast.success("Comment submitted for review");
        setCommentText("");
        setReplyingTo(null);
      } else {
        toast.error((await response.json()).detail || "Failed to submit comment");
      }
    } catch (err) {
      toast.error("Failed to submit comment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A4744]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-light text-[#2D2A26] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Article not found</h1>
          <Link to="/blog" className="text-[#A87C6D] hover:text-[#96695C]">← Back to Journal</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E8E4DE] bg-white/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/blog" className="flex items-center gap-2 text-[#4A4744] hover:text-[#2D2A26] transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              <span className="text-sm">Back to The Ritual Edit</span>
            </Link>
            {auth?.user ? (
              <Link to="/dashboard" className="px-4 py-2 text-sm text-[#4A4744] hover:text-[#2D2A26] transition-colors">Dashboard</Link>
            ) : (
              <Link to="/login" className="px-4 py-2 text-sm text-[#A87C6D] hover:text-[#96695C] transition-colors">Sign In</Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article>
          <div className="flex items-center gap-4 mb-6">
            {post.category && <span className="text-xs uppercase tracking-wider text-[#A87C6D] font-medium">{post.category}</span>}
            <span className="text-sm text-[#6E6A66]">{formatDate(post.published_at)}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2D2A26] mb-8 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{post.title}</h1>
          {post.featured_image && (
            <div className="rounded-2xl overflow-hidden mb-10 bg-gradient-to-br from-[#E6E2DD] to-[#A87C6D]/20 flex items-center justify-center">
              <img 
                src={post.featured_image.startsWith('http') ? post.featured_image : `${API.replace('/api', '')}${post.featured_image}`} 
                alt={post.title} 
                className="max-w-full max-h-[400px] w-auto h-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="aspect-[16/9] flex items-center justify-center"><span class="text-4xl opacity-50">✧</span></div>';
                }}
              />
            </div>
          )}
          <div 
            className="prose prose-lg max-w-none blog-content" 
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", lineHeight: 1.9, fontSize: '1.2rem', color: '#3D3A36' }} 
            dangerouslySetInnerHTML={{ __html: post.content }} 
          />
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-[#E8E4DE]">
              {post.tags.map(tag => <span key={tag} className="px-3 py-1 bg-[#F5F2EE] text-[#5A5652] text-sm rounded-full">#{tag}</span>)}
            </div>
          )}
        </article>

        <section className="mt-16 pt-12 border-t border-[#E8E4DE]">
          <h2 className="text-2xl font-light text-[#2D2A26] mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Comments</h2>
          {auth?.user ? (
            <form onSubmit={handleSubmitComment} className="mb-10">
              <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Share your thoughts..." rows={4} className="w-full px-5 py-4 rounded-xl border border-[#E8E4DE] focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D] resize-none" data-testid="comment-input" />
              <div className="flex justify-between items-center mt-3">
                <p className="text-xs text-[#6E6A66]">Comments are reviewed before appearing.</p>
                <button type="submit" disabled={submitting || !commentText.trim()} className="px-5 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-testid="submit-comment-btn">{submitting ? "Submitting..." : "Submit Comment"}</button>
              </div>
            </form>
          ) : (
            <div className="mb-10 p-6 bg-[#F9F7F4] rounded-xl text-center">
              <p className="text-[#4A4744] mb-3">Sign in to join the conversation</p>
              <Link to="/login" className="inline-flex px-5 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] transition-colors">Sign In</Link>
            </div>
          )}
          {comments.length > 0 ? (
            <div className="space-y-4">
              {comments.map(comment => <CommentItem key={comment.comment_id} comment={comment} depth={0} auth={auth} replyingTo={replyingTo} setReplyingTo={setReplyingTo} onSubmitReply={handleSubmitComment} submitting={submitting} formatDate={formatDate} />)}
            </div>
          ) : (
            <div className="text-center py-10"><p className="text-[#6E6A66]">No comments yet. Be the first to share your thoughts!</p></div>
          )}
        </section>
      </main>

      <footer className="border-t border-[#E8E4DE] bg-white/50 mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="text-sm text-[#4A4744] hover:text-[#A87C6D]">← The Soft Ritual</Link>
            <div className="flex gap-6">
              <Link to="/blog" className="text-sm text-[#4A4744] hover:text-[#A87C6D]">Ritual Edit</Link>
              <Link to="/store" className="text-sm text-[#4A4744] hover:text-[#A87C6D]">Store</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
